/**
 * bodyMotionExport — bake a kimodo tracks payload into a saveable body-animation
 * GLB (same on-disk shape as a converted FBX clip). Uses the same bodyMotionClip
 * baker as the live preview, so exported keyframes match what the rig plays. Root
 * motion is stored RELATIVE (delta from frame 0), never absolute world coords.
 * Exported hierarchy is BONES ONLY (no meshes/materials) so the file stays tiny;
 * tracks bind by node name, like any loaded GLB.
 */
import * as THREE from 'three'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { buildBodyMotionClip } from './bodyMotionClip.js'
import { resolveGeneratedRootMode } from './bodyMotionRootMode.js'
import { getLiveBodyRig } from './liveBodyRig.js'

/**
 * Export a kimodo tracks payload as a binary GLB carrying a single baked body
 * clip and the generation provenance (glTF scene extras).
 * @param {object} tracks kimodo /generate payload
 *   { fps, frames, bones:{name:[x,y,z,w,...]}, root?:[x,y,z,...] }
 * @param {object} [opts]
 * @param {string} [opts.name] clip + intended asset name
 * @param {boolean} [opts.keepRootMotion=true] keep the pelvis.position (travel)
 *   track; when false the clip is in-place (pelvis position never built).
 *   Defaults TRUE, matching bodyMotionRootMode's default and the wizard's "In place"
 *   checkbox being off — an omitted flag must not silently store a locked clip, which
 *   is the shape of the reported bug. Every caller states it explicitly today.
 * @param {object} [opts.provenance] { prompt, seed, duration_sec, diffusion_steps, generatedAt, waypoints, path_mode, walk_speed, timing_mode, constraint_stride, constraint_weight, smooth, corner_radius_m }
 * @returns {Promise<ArrayBuffer>} binary GLB bytes
 */
export async function exportBodyMotionGlb(tracks, { name, keepRootMotion = true, provenance = {} } = {}) {
  // The same two rigs the PREVIEW can play on (liveBodyRig.js): the GLB avatar's own
  // scene, or the splat's part rig — which declares itself here and has no
  // __AVATAR_SCENE__ at all, so reading that global alone made Save fail on exactly the
  // characters the preview had just worked on.
  const rig = getLiveBodyRig()
  const scene = (typeof window !== 'undefined' && window.__AVATAR_SCENE__) || rig?.scene
  if (!scene) throw new Error('exportBodyMotionGlb: avatar scene not ready')

  // The live preview may still be driving pelvis.position; buildBodyMotionClip
  // snapshots it as the rest pose, so a displaced pelvis would bake a permanent
  // offset into the travel track. Release the hold + stop the action first —
  // AnimationAction.stop() restores the saved rest synchronously. The hold's own
  // onExpire runs first, or a locomotion preview loses the root landing it was
  // holding (the part rig places the root every frame and commits on release).
  if (typeof window !== 'undefined') {
    const held = window.__DIRECT_PLAY__?.clipName === 'kimodo_generated' ? window.__DIRECT_PLAY__ : null
    if (held) {
      window.__DIRECT_PLAY__ = null
      if (typeof held.onExpire === 'function') {
        try { held.onExpire() } catch (err) { console.warn('[bodyMotionExport] preview onExpire threw:', err) }
      }
    }
    const preview = window.__AVATAR_ACTIONS__?.kimodo_generated
    if (preview) { try { preview.stop() } catch { /* mixer may be gone; rest read still safe */ } }
  }

  // Bake against the LIVE rig scene — root correction walks the pelvis ancestor
  // chain, so it must see the real nested transforms.
  //
  // The mode comes from the SHARED resolver (bodyMotionRootMode), the same one the
  // wizard's live preview calls. `live: false` — a stored clip has no avatar group to
  // carry its travel, so it always carries its own on the pelvis, and 'lock' never
  // builds that track in the first place. Filtering `pelvis.position` out afterwards is
  // what this used to do, and having the rule here as well as in the preview is how the
  // two came to disagree.
  const rootMode = resolveGeneratedRootMode({ keepRootMotion, live: false })
  const clip = buildBodyMotionClip(tracks, { scene, name, rootMode })

  // Skeleton-only export root: same node names + local transforms, no renderable
  // leaves. A real THREE.Scene (not a bare Object3D) so the provenance userData
  // lands in scene-level extras — GLTFLoader restores that to gltf.scene.userData,
  // which AvatarModel reads for the keepRootMotion loader exemption.
  // At the rig's REST pose where it captured one — the frame buildBodyMotionClip just
  // baked against. Cloning the live nodes would save whatever is posing the body at the
  // moment Save was pressed (on a splat, the body blueprint, every frame).
  const exportScene = cloneBonesOnlyScene(scene, rig?.scene === scene ? rig?.restPose : null)
  exportScene.userData.aosGeneratedMotion = {
    keepRootMotion,
    prompt: provenance.prompt ?? null,
    seed: provenance.seed ?? null,
    duration_sec: provenance.duration_sec ?? null,
    diffusion_steps: provenance.diffusion_steps ?? null,
    generatedAt: provenance.generatedAt ?? null,
    waypoints: provenance.waypoints ?? null,
    path_mode: provenance.path_mode ?? null,
    walk_speed: provenance.walk_speed ?? null,
    timing_mode: provenance.timing_mode ?? null,
    constraint_stride: provenance.constraint_stride ?? null,
    constraint_weight: provenance.constraint_weight ?? null,
    smooth: provenance.smooth ?? null,
    corner_radius_m: provenance.corner_radius_m ?? null,
  }

  return parseGlb(exportScene, clip)
}

/**
 * Bake the tracks (see exportBodyMotionGlb) and trigger a browser download of
 * the resulting GLB — no server round-trip. Mirrors downloadEnvironmentExport:
 * Blob → objectURL → anchor click → revoke. Returns { fileName }.
 * @param {object} tracks kimodo /generate payload
 * @param {object} [opts] same shape as exportBodyMotionGlb's opts
 * @returns {Promise<{ fileName: string }>}
 */
export async function downloadBodyMotionGlb(tracks, opts = {}) {
  const buf = await exportBodyMotionGlb(tracks, opts)
  const fileName = `${sanitizeFileBase(opts.name) || 'generated_motion'}.glb`
  const blob = new Blob([buf], { type: 'model/gltf-binary' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { fileName }
}

// Keep a user-supplied clip name safe as a filename base (drop path/reserved
// chars, collapse whitespace). Empty/undefined → '' so the caller's fallback wins.
function sanitizeFileBase(name) {
  return String(name ?? '').trim().replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_')
}

/**
 * Clone a scene keeping only transform nodes (bones/groups), dropping every
 * renderable leaf so the exported GLB carries no geometry/material/texture.
 * @param {THREE.Object3D} scene live avatar root
 * @returns {THREE.Scene}
 */
function cloneBonesOnlyScene(scene, restPose = null) {
  const out = new THREE.Scene()
  for (const child of scene.children) {
    const clone = cloneBonesOnly(child, restPose)
    if (clone) out.add(clone)
  }
  return out
}

function cloneBonesOnly(src, restPose) {
  // Renderable leaves carry the geometry/material/textures we deliberately omit.
  if (src.isMesh || src.isSkinnedMesh || src.isLine || src.isPoints || src.isSprite) return null
  const node = src.isBone ? new THREE.Bone() : new THREE.Object3D()
  node.name = src.name
  const rest = restPose?.get?.(src.name)
  if (rest) {
    node.position.fromArray(rest.position)
    node.quaternion.fromArray(rest.quaternion)
    node.scale.fromArray(rest.scale)
  } else {
    node.position.copy(src.position)
    node.quaternion.copy(src.quaternion)
    node.scale.copy(src.scale)
  }
  for (const child of src.children) {
    const clone = cloneBonesOnly(child, restPose)
    if (clone) node.add(clone)
  }
  return node
}

/**
 * Promise wrapper around GLTFExporter's callback-style binary parse.
 * @param {THREE.Scene} root
 * @param {THREE.AnimationClip} clip
 * @returns {Promise<ArrayBuffer>}
 */
function parseGlb(root, clip) {
  return new Promise((resolve, reject) => {
    new GLTFExporter().parse(
      root,
      (result) => resolve(result),
      (err) => reject(err instanceof Error ? err : new Error('GLTFExporter failed: ' + err)),
      { binary: true, animations: [clip], onlyVisible: false },
    )
  })
}
